import styled from '@emotion/styled';
import CircleIcon from '@mui/icons-material/Circle';
import MuiChip from '@mui/material/Chip';
import { ReactElement } from 'react';
import {
  BADGE_TYPE,
  BadgeStatus,
  badgeStatusValues,
  BadgeType,
} from 'src/common/components/StatusBadge/StatusBadgeConstants';
import { pxToRem } from 'src/common/utils/fonts';
import { palette } from 'theme/palette';

type StatusBadgeProps = {
  status: BadgeStatus;
  type: BadgeType;
  label?: string;
  icon?: ReactElement;
  hideIcon?: boolean;
};

const colorMap = {
  success: {
    backgroundColor: palette.success.main,
    color: palette.success.contrastText,
    '& .MuiChip-icon': {
      color: palette.success.dark,
    },
  },
  warning: {
    backgroundColor: palette.warning.main,
    '& .MuiChip-icon': {
      color: palette.warning.dark,
    },
  },
  error: {
    backgroundColor: palette.error.main,
    color: palette.error.contrastText,
    '& .MuiChip-icon': {
      color: palette.error.dark,
    },
  },
  terminated: {
    backgroundColor: palette.text.primary,
    color: palette.text.primary,
    '& .MuiChip-icon': {
      color: palette.text.primary,
    },
  },
  pending: {
    backgroundColor: palette.info.main,
    color: palette.info.contrastText,
    '& .MuiChip-icon': {
      color: palette.info.dark,
    },
  },
  inactive: {
    backgroundColor: palette.background.default,
    color: palette.text.secondary,
    '& .MuiChip-icon': {
      color: palette.text.tertiary,
    },
  },
  unknown: {
    backgroundColor: palette.background.default,
    color: palette.text.secondary,
    '& .MuiChip-icon': {
      color: palette.grey['100'],
      border: `1px solid ${palette.text.tertiary}`,
      borderRadius: pxToRem(10),
      marginTop: pxToRem(-2),
    },
  },
};

const StyledChip = styled(MuiChip)(({ status, type }: StatusBadgeProps) => {
  if (badgeStatusValues.includes(status)) {
    return {
      height: pxToRem(28),
      borderRadius: pxToRem(16),
      ...(type === BADGE_TYPE.TEXT ? {} : colorMap[status]),
      '& .MuiChip-icon': {
        marginRight: pxToRem(4),
        marginLeft: 0,
        ...colorMap[status]?.['& .MuiChip-icon'],
      },
    };
  } else {
    return {};
  }
});

export const StatusBadge = ({
  status,
  type = 'filled',
  label,
  icon,
  hideIcon,
}: StatusBadgeProps) => {
  const iconToDisplay = icon || (
    <CircleIcon
      sx={{
        fontSize: pxToRem(10),
        marginLeft: '0px!important',
        marginTop: pxToRem(-1),
        marginRight: `${pxToRem(4)}!important`,
      }}
    />
  );

  return (
    <StyledChip
      sx={{
        ...(status && !label ? { textTransform: 'capitalize' } : {}),
        ...(type === BADGE_TYPE.TEXT
          ? { border: 'none', backgroundColor: 'transparent!important' }
          : {}),
      }}
      icon={hideIcon ? undefined : iconToDisplay}
      label={label || status}
      type={type}
      status={status}
    />
  );
};
